import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { ColDef, ValueFormatterParams, ValueParserParams, CellValueChangedEvent, GridApi } from 'ag-grid-community'
import { supabase } from '../../lib/supabase'
import { ABBEntry, MarketSettings, PendingChange, RowData } from './useABBData'

interface UseABBGridParams {
  modelId: string | undefined
  selectedYearModel: number | null
  abbData: ABBEntry[]
  marketSettings: MarketSettings | null
  fetchABBData: (settings: MarketSettings) => Promise<void>
}

export function useABBGrid({
  modelId,
  selectedYearModel,
  abbData,
  marketSettings,
  fetchABBData,
}: UseABBGridParams) {
  const [saving, setSaving] = useState(false)
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([])
  const [batchSaveStatus, setBatchSaveStatus] = useState<string | null>(null)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  const gridApiRef = useRef<GridApi | null>(null)
  const contextMenuRef = useRef<HTMLDivElement>(null)

  // Batch save function - saves all pending changes at once
  const executeBatchSave = useCallback(async (changes: PendingChange[]) => {
    if (changes.length === 0) return
    try {
      setSaving(true)
      setBatchSaveStatus(`Saving ${changes.length} value${changes.length > 1 ? 's' : ''}...`)

      const toInsert: { model_id: number; year_model: number; year: number; quarter: number; value: number }[] = []
      const toUpdate: { abb_id: string; value: number }[] = []
      const toDelete: string[] = []

      for (const change of changes) {
        const existingEntry = abbData.find(
          d => d.year_model === selectedYearModel && d.year === change.year && d.quarter === change.quarter
        )
        if (existingEntry) {
          if (change.value === null) {
            toDelete.push(existingEntry.abb_id!)
          } else {
            toUpdate.push({ abb_id: existingEntry.abb_id!, value: change.value })
          }
        } else if (change.value !== null) {
          toInsert.push({
            model_id: parseInt(modelId!, 10),
            year_model: selectedYearModel!,
            year: change.year,
            quarter: change.quarter,
            value: change.value
          })
        }
      }

      if (toDelete.length > 0) {
        const { error } = await supabase.from('abb').delete().in('abb_id', toDelete)
        if (error) throw error
      }
      if (toUpdate.length > 0) {
        const updatePromises = toUpdate.map(item =>
          supabase.from('abb').update({ value: item.value }).eq('abb_id', item.abb_id)
        )
        const results = await Promise.all(updatePromises)
        const errors = results.filter(r => r.error)
        if (errors.length > 0) throw errors[0].error
      }
      if (toInsert.length > 0) {
        const { error } = await supabase.from('abb').insert(toInsert)
        if (error) throw error
      }

      if (marketSettings) {
        await fetchABBData(marketSettings)
      }
      setBatchSaveStatus(`Saved ${changes.length} value${changes.length > 1 ? 's' : ''} successfully!`)
      setTimeout(() => setBatchSaveStatus(null), 2000)
    } catch (err: any) {
      setBatchSaveStatus(`Error saving: ${err.message}`)
      setTimeout(() => setBatchSaveStatus(null), 3000)
    } finally {
      setSaving(false)
      setPendingChanges([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abbData, modelId, marketSettings, selectedYearModel])

  // Cell value changed handler - queues changes for batch save
  const handleCellValueChanged = useCallback((event: CellValueChangedEvent) => {
    const { data, colDef, newValue, oldValue } = event
    const year = data.year
    const quarter = colDef.field === 'q1' ? 1 : colDef.field === 'q2' ? 2 : colDef.field === 'q3' ? 3 : 4
    const change: PendingChange = { year, quarter, value: newValue, oldValue }
    setPendingChanges(prev => {
      const filtered = prev.filter(c => !(c.year === year && c.quarter === quarter))
      return [...filtered, change]
    })
  }, [])

  // Manual save handler
  const handleSaveChanges = useCallback(() => {
    if (pendingChanges.length > 0) {
      executeBatchSave(pendingChanges)
    }
  }, [pendingChanges, executeBatchSave])

  // Discard changes
  const handleDiscardChanges = useCallback(() => {
    if (pendingChanges.length === 0) return
    if (!window.confirm(`Discard ${pendingChanges.length} unsaved change${pendingChanges.length > 1 ? 's' : ''}?`)) {
      return
    }
    if (marketSettings) {
      fetchABBData(marketSettings)
    }
    setPendingChanges([])
    setBatchSaveStatus(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingChanges, marketSettings])

  // Parse a clipboard value - strip formatting
  const parseClipboardValue = useCallback((value: string): number | null => {
    if (value === null || value === undefined || value === '') return null
    const cleaned = String(value).replace(/[$,\s]/g, '').trim()
    if (cleaned === '' || cleaned === '-') return null
    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? null : parsed
  }, [])

  // Custom paste handler for multi-cell paste from Excel
  const handlePaste = useCallback(async (event: ClipboardEvent) => {
    const clipboardData = event.clipboardData
    if (!clipboardData || !gridApiRef.current || !selectedYearModel) return

    const pastedText = clipboardData.getData('text/plain')
    if (!pastedText) return

    const focusedCell = gridApiRef.current.getFocusedCell()
    if (!focusedCell) return
    if (focusedCell.column.getColId() === 'year') return

    event.preventDefault()

    const rows = pastedText.split(/\r?\n/).filter(row => row.trim() !== '')
    const detectDelimiter = (_text: string): RegExp => {
      const firstRow = rows[0] || ''
      if (firstRow.includes('\t')) return /\t/
      if (firstRow.includes(',')) return /,/
      if (firstRow.includes(';')) return /;/
      return /\s{2,}/
    }
    const delimiter = detectDelimiter(pastedText)

    const parsedData: (number | null)[][] = rows.map(row =>
      row.split(delimiter).map(cell => parseClipboardValue(cell))
    )
    if (parsedData.length === 0) return

    const columnOrder = ['q1', 'q2', 'q3', 'q4']
    const startColId = focusedCell.column.getColId()
    const startColIndex = columnOrder.indexOf(startColId)
    if (startColIndex === -1) return

    const allRowNodes: any[] = []
    gridApiRef.current.forEachNode(node => allRowNodes.push(node))
    const startRowIndex = focusedCell.rowIndex

    const changes: PendingChange[] = []
    const allValues: (number | null)[] = parsedData.flat()

    let currentRowIndex = startRowIndex
    let currentColIndex = startColIndex
    let valueIndex = 0

    while (valueIndex < allValues.length && currentRowIndex < allRowNodes.length) {
      const cellValue = allValues[valueIndex]
      const rowNode = allRowNodes[currentRowIndex]
      const year = rowNode.data.year
      const colId = columnOrder[currentColIndex]
      const quarter = currentColIndex + 1
      const oldValue = rowNode.data[colId]

      rowNode.setDataValue(colId, cellValue)
      changes.push({ year, quarter, value: cellValue, oldValue })

      valueIndex++
      currentColIndex++
      if (currentColIndex >= columnOrder.length) {
        currentColIndex = 0
        currentRowIndex++
      }
    }

    gridApiRef.current.refreshCells()

    if (changes.length > 0) {
      setPendingChanges(prev => {
        const newChanges = [...prev]
        for (const change of changes) {
          const existingIndex = newChanges.findIndex(
            c => c.year === change.year && c.quarter === change.quarter
          )
          if (existingIndex >= 0) {
            newChanges[existingIndex] = change
          } else {
            newChanges.push(change)
          }
        }
        return newChanges
      })
      setBatchSaveStatus(`Pasted ${changes.length} value${changes.length > 1 ? 's' : ''} - click Save to apply`)
      setTimeout(() => setBatchSaveStatus(null), 3000)
    }
  }, [selectedYearModel, parseClipboardValue])

  // Add paste event listener to the grid container
  useEffect(() => {
    const gridContainer = document.querySelector('.ag-root-wrapper')
    if (gridContainer) {
      const pasteHandler = (event: Event) => handlePaste(event as ClipboardEvent)
      gridContainer.addEventListener('paste', pasteHandler)
      return () => {
        gridContainer.removeEventListener('paste', pasteHandler)
      }
    }
  }, [handlePaste])

  // Handle right-click context menu
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    setContextMenu({ x: event.clientX, y: event.clientY })
  }, [])

  // Handle paste from context menu
  const handleContextMenuPaste = useCallback(async () => {
    setContextMenu(null)
    if (!gridApiRef.current || !selectedYearModel) return
    try {
      const clipboardText = await navigator.clipboard.readText()
      if (!clipboardText) return
      const syntheticEvent = {
        clipboardData: { getData: () => clipboardText },
        preventDefault: () => {}
      } as unknown as ClipboardEvent
      handlePaste(syntheticEvent)
    } catch (err) {
      setBatchSaveStatus('Unable to access clipboard. Please use Ctrl+V / Cmd+V instead.')
      setTimeout(() => setBatchSaveStatus(null), 3000)
    }
  }, [selectedYearModel, handlePaste])

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(null)
      }
    }
    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [contextMenu])

  // Column definitions
  const columnDefs = useMemo<ColDef[]>(() => {
    const quarterCol = (field: string, headerName: string): ColDef => ({
      field,
      headerName,
      flex: 1,
      editable: true,
      sortable: false,
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value !== null ? `$${params.value.toLocaleString()}` : ''
      },
      valueParser: (params: ValueParserParams) => {
        if (params.newValue === null || params.newValue === undefined || params.newValue === '') return null
        const strValue = String(params.newValue).replace(/[$,]/g, '')
        return strValue ? parseFloat(strValue) : null
      }
    })
    return [
      { field: 'year', headerName: 'Year', flex: 1, pinned: 'left' as const, cellClass: 'font-medium', editable: false, sortable: false },
      quarterCol('q1', 'Q1'),
      quarterCol('q2', 'Q2'),
      quarterCol('q3', 'Q3'),
      quarterCol('q4', 'Q4'),
    ]
  }, [])

  return {
    saving,
    pendingChanges,
    batchSaveStatus,
    contextMenu,
    gridApiRef,
    contextMenuRef,
    columnDefs,
    handleCellValueChanged,
    handleSaveChanges,
    handleDiscardChanges,
    handleContextMenu,
    handleContextMenuPaste,
  }
}
